import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useMountedState } from 'react-use';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  getInferNameMatchCandidatesFromOptions,
  inferInsurancePolicyKind,
  inferNameMatch,
} from '@/modules/assetProviderIntegrations/shared/integrationInference';

import { LifeInsuranceStepFormShape } from '../LifeInsuranceStep.types';
import { useLifeInsuranceStepData } from './useLifeInsuranceStepData';

interface UseLifeInsuranceInferenceOptions {
  formMethods: Pick<
    UseFormReturn<LifeInsuranceStepFormShape>,
    'control' | 'getFieldState' | 'getValues' | 'setValue'
  >;
  integrationInsurances: ReturnType<
    typeof useLifeInsuranceStepData
  >['integrationInsurances'];
  loading: boolean;
  policyHolderOptions: TypeaheadSelectInputOption<string>[];
}

export function useLifeInsuranceInference({
  formMethods,
  integrationInsurances,
  loading,
  policyHolderOptions,
}: UseLifeInsuranceInferenceOptions) {
  const client = useApolloClient();
  const policies = useWatch({ control: formMethods.control, name: 'policies' });
  const [inferringPolicyIds, setInferringPolicyIds] = useState<
    Record<string, boolean>
  >({});
  const attemptedPolicyIds = useRef(new Set<string>());
  const isMounted = useMountedState();
  const policyHolderCandidates = useMemo(
    () => getInferNameMatchCandidatesFromOptions(policyHolderOptions),
    [policyHolderOptions]
  );

  useEffect(() => {
    if (loading) return;

    const inferPolicy = async (
      insurance: (typeof integrationInsurances)[number]
    ) => {
      if (!formMethods.getValues(`policies.${insurance.id}.shouldImport`)) {
        return;
      }
      const policyTypeName = `policies.${insurance.id}.policyType` as const;
      const policyHolderName =
        `policies.${insurance.id}.policyHolderIDs.0` as const;
      const fields = [policyTypeName, policyHolderName] as const;
      const canSet = (name: (typeof fields)[number]) =>
        !formMethods.getValues(name) &&
        !formMethods.getFieldState(name).isDirty;

      if (
        !fields.some(canSet) ||
        attemptedPolicyIds.current.has(insurance.id)
      ) {
        return;
      }
      attemptedPolicyIds.current.add(insurance.id);
      setInferringPolicyIds((current) => ({
        ...current,
        [insurance.id]: true,
      }));

      try {
        const [policyType, policyHolderId] = await Promise.all([
          canSet(policyTypeName)
            ? inferInsurancePolicyKind({ client, input: insurance.policyType })
            : null,
          canSet(policyHolderName)
            ? inferNameMatch({
                client,
                name: insurance.policyHolderName,
                candidates: policyHolderCandidates,
              })
            : null,
        ]);

        if (
          !isMounted() ||
          !formMethods.getValues(`policies.${insurance.id}.shouldImport`)
        ) {
          return;
        }
        if (policyType && canSet(policyTypeName)) {
          formMethods.setValue(policyTypeName, policyType);
        }
        if (policyHolderId && canSet(policyHolderName)) {
          formMethods.setValue(policyHolderName, policyHolderId);
        }
      } finally {
        if (isMounted()) {
          setInferringPolicyIds((current) => ({
            ...current,
            [insurance.id]: false,
          }));
        }
      }
    };

    void Promise.allSettled(integrationInsurances.map(inferPolicy));
  }, [
    client,
    formMethods,
    integrationInsurances,
    isMounted,
    loading,
    policies,
    policyHolderCandidates,
  ]);

  return inferringPolicyIds;
}
